import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { House, CalendarDays, ChartLine, MessageCircle, User } from "lucide-react";
import { Unlock } from "@/components/program/Unlock";
import { Onboarding } from "@/components/program/Onboarding";
import { Generation } from "@/components/program/Generation";
import { Today } from "@/components/program/Today";
import { Plan } from "@/components/program/Plan";
import { Progress } from "@/components/program/Progress";
import { Coach } from "@/components/program/Coach";
import { Profile } from "@/components/program/Profile";
import { Disclaimer } from "@/components/program/shared";

const NAV = [["today", "Today", House], ["plan", "Plan", CalendarDays], ["progress", "Progress", ChartLine], ["coach", "Coach", MessageCircle], ["profile", "Profile", User]];

const BottomNav = ({ view, setView }) => (
  <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[#05070d]/90 backdrop-blur-xl border-t border-white/10" data-testid="program-bottom-nav">
    <div className="max-w-5xl mx-auto grid grid-cols-5">
      {NAV.map(([k, l, I]) => (
        <button key={k} onClick={() => setView(k)} data-testid={`program-nav-${k}`}
          className={`flex flex-col items-center gap-1 py-3 text-[10px] font-mono uppercase tracking-wider transition-colors ${view === k ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}>
          <I size={18} /> {l}
        </button>
      ))}
    </div>
  </nav>
);

export default function MyProgram() {
  const [ent, setEnt] = useState(null);
  const [view, setView] = useState("today");
  const [onboardingId, setOnboardingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/programs/entitlements"); setEnt(data); }
    catch { setEnt({ accesses: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!ent) return <div className="pt-40 text-center font-mono text-xs text-cyan-300 animate-pulse tracking-widest">LOADING YOUR PROGRAM…</div>;

  const accesses = ent.accesses || [];
  const active = accesses.find((a) => ["ACTIVE", "PAUSED"].includes(a.status));
  const generating = accesses.find((a) => a.status === "ONBOARDING" && a.generation_status && a.generation_status !== "complete");
  const review = accesses.find((a) => a.status === "REQUIRES_REVIEW");
  const onboarding = onboardingId
    ? accesses.find((a) => a.id === onboardingId && a.status === "ONBOARDING")
    : accesses.find((a) => a.status === "ONBOARDING" && !a.generation_status);

  if (active) {
    const A = active.id;
    return (
      <div className="pt-[76px] pb-24 min-h-screen bg-[#04060b]" data-testid="my-program">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          {view === "today" && <Today accessId={A} setView={setView} />}
          {view === "plan" && <Plan accessId={A} />}
          {view === "progress" && <Progress accessId={A} />}
          {view === "coach" && <Coach accessId={A} />}
          {view === "profile" && <Profile accessId={A} access={active} ent={ent} onChange={load} />}
        </div>
        <BottomNav view={view} setView={setView} />
      </div>
    );
  }
  if (generating) return <Generation accessId={generating.id} onReady={load} />;
  if (onboarding) return <Onboarding access={onboarding} onDone={() => { setOnboardingId(null); load(); }} />;
  if (review) return (
    <div className="pt-40 text-center max-w-md mx-auto px-6" data-testid="program-review">
      <h1 className="font-serif text-3xl text-white mb-3">Your intake is under review</h1>
      <p className="text-sm text-slate-400">Thanks for the detail you shared. The CG Labz Performance Team is reviewing a few responses to make sure your plan is built safely. We&apos;ll notify you shortly.</p>
      <Disclaimer className="mt-8 justify-center text-left" />
    </div>
  );
  return <Unlock ent={ent} onClaimed={(id) => { setOnboardingId(id); load(); }} onChange={load} />;
}
